import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';

import type { AdminProject } from 'features/projects';
import { Info, Play, Search, TriangleAlert } from 'lucide-react';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import { useCreateRunMutation, usePreviewRunMutation } from '../../api/runs';
import {
  AGENT_CALLS_PER_SCENARIO,
  JUDGE_CALLS_PER_SCENARIO,
  PROMPT_LABEL_LABELS,
  formatUsd,
  type AgentTestRunPromptLabel,
  type PreviewRunResult,
} from '../../model/run';
import type { AgentTestScenario, AgentTestScope } from '../../model/scenario';
import InfoHint from './InfoHint';

const PREVIEW_DEBOUNCE_MS = 500;
const CREATE_ERROR_FALLBACK = 'No se pudo crear el run. Intenta nuevamente.';

const GROUP_ORDER: AgentTestScope[] = ['global', 'common', 'subProject'];
const GROUP_TITLES: Record<AgentTestScope, string> = {
  global: 'Globales',
  common: 'Inmobiliaria',
  subProject: 'Proyectos',
};

interface RunFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  masterProject: { id: number; name: string };
  subProjects: AdminProject[];
  scenarios: AgentTestScenario[];
}

const RunFormDialog = ({ open, onOpenChange, masterProject, scenarios }: RunFormDialogProps): JSX.Element => {
  const [name, setName] = useState('');
  const [promptLabel, setPromptLabel] = useState<AgentTestRunPromptLabel>('production');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const [preview, setPreview] = useState<PreviewRunResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [previewRun] = usePreviewRunMutation();
  const [createRun, { isLoading: isCreating }] = useCreateRunMutation();

  useEffect(() => {
    if (!open) return;
    setName('');
    setPromptLabel('production');
    setQuery('');
    setSubmitError(null);
    setPreview(null);
    setSelected(new Set(scenarios.filter((scenario) => scenario.enabled).map((scenario) => scenario.id)));
  }, [open, scenarios]);

  useEffect(() => {
    if (!open || selected.size === 0) {
      setPreview(null);
      return;
    }
    const scenarioIds = [...selected];
    const handle = setTimeout(() => {
      previewRun({ projectId: masterProject.id, scenarios: scenarioIds })
        .unwrap()
        .then(setPreview)
        .catch(() => setPreview(null));
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [open, selected, masterProject.id, previewRun]);

  const enabledScenarios = useMemo(() => scenarios.filter((scenario) => scenario.enabled), [scenarios]);

  const filtered = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (trimmed.length === 0) return enabledScenarios;
    return enabledScenarios.filter((scenario) => scenario.name.toLowerCase().includes(trimmed));
  }, [enabledScenarios, query]);

  const toggleScenario = (scenarioId: number, checked: boolean): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(scenarioId);
      else next.delete(scenarioId);
      return next;
    });
  };

  const toggleGroup = (group: AgentTestScenario[], checked: boolean): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const scenario of group) {
        if (checked) next.add(scenario.id);
        else next.delete(scenario.id);
      }
      return next;
    });
  };

  const handleCreate = async (): Promise<void> => {
    setSubmitError(null);
    try {
      await createRun({
        ...(name.trim().length > 0 ? { name: name.trim() } : {}),
        projectId: masterProject.id,
        promptLabel,
        scenarios: [...selected],
      }).unwrap();
      onOpenChange(false);
    } catch (err) {
      setSubmitError(extractErrorMessage(err, CREATE_ERROR_FALLBACK));
    }
  };

  const agentCalls = preview?.agentCalls ?? selected.size * AGENT_CALLS_PER_SCENARIO;
  const judgeCalls = preview?.judgeCalls ?? selected.size * JUDGE_CALLS_PER_SCENARIO;
  const overLimit = preview !== null && (!preview.withinBudget || !preview.withinScenarioLimit);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuevo run</DialogTitle>
          <DialogDescription>
            Selecciona los escenarios de {masterProject.name} que se ejecutarán en este run.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-text-default">Nombre</span>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="p. ej. Run semanal de escenarios de precio"
            aria-label="Nombre del run"
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="flex items-center gap-1 text-sm font-medium text-text-default">
            Versión del prompt
            <InfoHint text="Producción corre contra el prompt que sirve a los clientes hoy; Latest contra la última versión guardada, para validar cambios antes de promoverla." />
          </span>
          <div className="flex w-fit gap-0.5 rounded-lg bg-bg-surface-alt p-0.5">
            {(['production', 'latest'] as const).map((label) => (
              <button
                key={label}
                type="button"
                onClick={() => setPromptLabel(label)}
                className={cn(
                  'rounded-md px-3.5 py-1.5 text-[0.8rem] font-medium transition-colors',
                  promptLabel === label ? 'bg-bg-surface text-text-default shadow-sm' : 'text-text-muted'
                )}
              >
                {PROMPT_LABEL_LABELS[label]}
              </button>
            ))}
          </div>
        </div>

        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-text-muted"
            aria-hidden
          />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="pl-9"
            placeholder="Buscar escenario…"
            aria-label="Buscar escenario"
          />
        </div>

        <div className="flex max-h-[320px] flex-col gap-3 overflow-y-auto pr-1">
          {GROUP_ORDER.map((scope) => {
            const group = filtered.filter((scenario) => scenario.scope === scope);
            if (group.length === 0) return null;

            const selectedCount = group.filter((scenario) => selected.has(scenario.id)).length;
            const groupChecked: boolean | 'indeterminate' =
              selectedCount === 0 ? false : selectedCount === group.length ? true : 'indeterminate';

            return (
              <div key={scope} className="flex flex-col rounded-lg border border-border-subtle">
                <div className="flex items-center gap-2.5 rounded-t-lg bg-bg-surface-raised px-3 py-2">
                  <Checkbox
                    checked={groupChecked}
                    onCheckedChange={(checked) => toggleGroup(group, checked === true)}
                    aria-label={`Seleccionar todos los escenarios de ${GROUP_TITLES[scope]}`}
                  />
                  <span className="text-xs font-semibold text-text-default">{GROUP_TITLES[scope]}</span>
                  <span className="text-[0.7rem] text-text-muted">
                    {selectedCount} de {group.length} seleccionados
                  </span>
                </div>
                {group.map((scenario, index) => {
                  const isSelected = selected.has(scenario.id);
                  return (
                    <div
                      key={scenario.id}
                      className={cn(
                        'flex items-center gap-2.5 py-2 pr-3 pl-6',
                        index < group.length - 1 && 'border-b border-border-subtle'
                      )}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => toggleScenario(scenario.id, checked === true)}
                        aria-label={`Incluir «${scenario.name}»`}
                      />
                      <span
                        className={cn('truncate text-sm', isSelected ? 'text-text-default' : 'text-text-muted')}
                        title={scenario.name}
                      >
                        {scenario.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
          {filtered.length === 0 ? (
            <p className="m-0 py-6 text-center text-sm text-text-muted">
              No hay escenarios habilitados que coincidan con la búsqueda.
            </p>
          ) : null}
        </div>

        <div
          className={cn(
            'flex items-start gap-2 rounded-md border px-3 py-2',
            overLimit ? 'border-warning/40 bg-warning-light' : 'border-brand-vivid/30 bg-surface-info'
          )}
        >
          {overLimit ? (
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
          ) : (
            <Info className="mt-0.5 size-4 shrink-0 text-brand-vivid" aria-hidden />
          )}
          <div className="flex flex-col gap-0.5">
            <p className="m-0 text-sm font-semibold text-text-default">
              {selected.size} {selected.size === 1 ? 'escenario seleccionado' : 'escenarios seleccionados'}
              {preview?.estimatedCostUsd != null ? ` · ~${formatUsd(preview.estimatedCostUsd)}` : ''}
            </p>
            <p className="m-0 text-sm text-text-default">
              ~{agentCalls} llamadas al agente + ~{judgeCalls} llamadas de juez
            </p>
            {preview !== null && !preview.withinScenarioLimit ? (
              <p className="m-0 text-xs text-warning">Supera el tope de {preview.maxScenarios} escenarios por run.</p>
            ) : null}
            {preview !== null && !preview.withinBudget ? (
              <p className="m-0 text-xs text-warning">
                El costo estimado supera el presupuesto máximo de {formatUsd(preview.maxBudgetUsd)}.
              </p>
            ) : null}
          </div>
        </div>

        {submitError ? (
          <p className="m-0 rounded-md border border-error/30 bg-error-light px-3 py-2 text-sm text-error" role="alert">
            {submitError}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isCreating}>
            Cancelar
          </Button>
          <Button type="button" disabled={selected.size === 0 || isCreating} onClick={() => void handleCreate()}>
            <Play className="size-4" aria-hidden />
            {isCreating ? 'Creando…' : 'Crear run'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RunFormDialog;
